import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Map, ZoomControl, Overlay } from "pigeon-maps"
import { getAirportUuidAndCoordinates, SelectCurrentCoordinates } from "_redux/slices/timeline/timeline-live-location-tab";
import { selectFirebaseInfo } from "_redux/slices/main-screen/user";
import { TbWheelchair } from "react-icons/tb";
import { FaRegCompass } from "react-icons/fa";
import { MdSatelliteAlt, MdMap } from "react-icons/md";
import { buildCentroids, bleToCoords, resolveLocationLabel } from './asset-tracking-utils';
import { selectAirportLocations } from '_redux/slices/timeline/timeline-areas';

const icons = {
    'Wheelchair-Tracking': TbWheelchair,
    'carts': TbWheelchair,
};

const SATELLITE_PROVIDER = (x, y, z) =>
    `https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

const OSM_PROVIDER = (x, y, z) =>
    `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;

const POS_BADGE = {
    bluetooth: { bg: '#dbeafe', color: '#1e4fa0', label: 'BLE'  },
    gps:       { bg: '#dcfce7', color: '#15623a', label: 'GPS'  },
    wifi:      { bg: '#ede9fe', color: '#5b21b6', label: 'WiFi' },
};

const LEGEND_ITEMS = [
    { color: '#007bff', label: 'Fixed' },
    { color: '#36ed6d', label: 'Moving' },
    { color: '#7eb8e0', label: 'Stopped' },
    { color: '#f0a030', label: 'No Fix' },
    { color: '#808080', label: 'Offline' },
];

// Lower index = shown when mixed states (most active wins)
const COLOR_PRIORITY = ['#36ed6d', '#f0a030', '#007bff', '#7eb8e0', '#808080'];

function pickDominantColor(colors) {
    let best = colors[0];
    let bestIdx = COLOR_PRIORITY.indexOf(best);
    if (bestIdx === -1) bestIdx = 999;
    for (const c of colors) {
        const idx = COLOR_PRIORITY.indexOf(c);
        const eff = idx === -1 ? 999 : idx;
        if (eff < bestIdx) { best = c; bestIdx = eff; }
    }
    return best;
}

const FIVE_MINUTES_MS = 5 * 60 * 1000;

function isDeviceStale(device, now) {
    const lastMotion = device?.meta?.deviceLastMotionAt;
    if (!lastMotion) return false;
    return (now - lastMotion.toDate().getTime()) > FIVE_MINUTES_MS;
}

function AssetTrackingMap(props) {

    const [mapCenter, setMapCenter] = useState([0, 0]);
    const [zoom, setZoom] = useState(18);
    const [activeKey, setActiveKey] = useState(null);
    const [clusterModal, setClusterModal] = useState(null); // { items, count }
    const [now, setNow] = useState(Date.now());
    const locations = useSelector(selectAirportLocations) ?? [];
    const gateways = props.gateways ?? [];
    const [satelliteView, setSatelliteView] = useState(false);

    const dispatch = useDispatch();
    const dbUser = useSelector(selectFirebaseInfo);
    const airportCoordinates = useSelector(SelectCurrentCoordinates);

    useEffect(() => {
        if (airportCoordinates?.lat && airportCoordinates?.lon) return;
        dispatch(getAirportUuidAndCoordinates(dbUser?.details.authority));
    }, []);

    useEffect(() => {
        if (airportCoordinates?.lat && airportCoordinates?.lon) {
            setMapCenter([parseFloat(airportCoordinates.lat), parseFloat(airportCoordinates.lon)]);
        }
    }, [airportCoordinates]);

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 10000);
        return () => clearInterval(id);
    }, []);

    const resetMap = () => {
        if (airportCoordinates?.lat && airportCoordinates?.lon) {
            setMapCenter([parseFloat(airportCoordinates.lat), parseFloat(airportCoordinates.lon)]);
            setZoom(18);
        }
    };

    // Build centroids for GPS location resolution
    const centroids = useMemo(() => buildCentroids(locations), [locations]);

    // Pass 1: resolve every device to { device, lat, lon, markerColor, info }
    const resolvedDevices = useMemo(() => {
        const wifiLocations = props.wifiLocations || {};
        return (props.devices || []).map((device) => {
            let lat = null;
            let lon = null;
            let bleItems = device?.latest?.bleItems || [];
            let batteryLow = device?.meta?.batteryLow;
            let batteryPct = device?.meta?.batteryPct ?? null;
            let lastActive = device?.meta?.deviceLastMotionAt;
            let deviceTemp = device?.meta?.deviceTemp;
            let payloadType = device?.latest?.payloadType;
            let eventType = device?.latest?.eventType;
            let operationMode = device?.latest?.operationMode;
            let gatewayId = device?.meta?.gatewayId;
            let positioningType = device?.latest?.positioningType;
            let stillInMotion = device?.latest?.stillInMotion;
            let positioningFailureReason = device?.latest?.positioningFailureReason;

            // Resolve gateway name from the shared gateways list
            const gwName = gateways.find(g => g.gatewayId === gatewayId)?.name ?? gatewayId ?? 'N/A';

            // Resolve human-readable location label (same logic as Locations tab)
            const locInfo = resolveLocationLabel(device, locations, centroids, wifiLocations);
            const locLabel = locInfo
                ? locInfo.source === 'last-known'
                    ? `${locInfo.label} (last known)`
                    : locInfo.label
                : 'Unknown';

            let markerColor = '#007bff';
            let info = {
                'Location': locLabel,
                'Last Movement': lastActive ? lastActive.toDate().toLocaleString() : 'N/A',
                'Battery': batteryPct !== null ? `${batteryPct}%` : batteryLow ? 'Low' : 'OK',
                'Temperature': deviceTemp ? `${deviceTemp} °C` : 'N/A',
                'Operation Mode': operationMode || 'N/A',
                'Nearest Gateway': gwName,
            };

            const resolveLvp = (lvp) => {
                if (!lvp) return;
                if (lvp.positioningType === 'gps') {
                    lat = lvp.gpsData?.latitude;
                    lon = lvp.gpsData?.longitude;
                } else if (lvp.positioningType === 'wifi') {
                    const w = wifiLocations[device.id];
                    if (w?.located) { lat = w.lat; lon = w.lng; }
                } else if (lvp.positioningType === 'bluetooth') {
                    const c = bleToCoords(lvp.bleItems, locations);
                    if (c) { lat = c.latitude; lon = c.longitude; }
                }
            };

            if (payloadType === "heartbeat") {
                info['In Motion'] = stillInMotion ? "Yes" : "No";
                resolveLvp(device?.lastValidPosition);
            } else if (payloadType === "location_fixed" || payloadType === "location_fixed_gps_limit") {
                if (positioningType === "gps") {
                    lat = device?.latest?.gpsData?.latitude ?? null;
                    lon = device?.latest?.gpsData?.longitude ?? null;
                } else if (positioningType === "bluetooth") {
                    const c = bleToCoords(bleItems, locations);
                    if (c) { lat = c.latitude; lon = c.longitude; }
                } else if (positioningType === "wifi") {
                    const w = wifiLocations[device.id];
                    if (w?.located) { lat = w.lat; lon = w.lng; }
                }
                if (!lat || !lon) resolveLvp(device?.lastValidPosition);
                markerColor = stillInMotion ? '#36ed6d' : '#007bff';
            } else if (payloadType === "location_failure") {
                info['Failure Reason'] = positioningFailureReason || 'N/A';
                markerColor = '#f0a030';
                resolveLvp(device?.lastValidPosition);
            } else if (payloadType === "shutdown") {
                info['In Motion'] = "No";
                markerColor = '#808080';
                if (positioningType === "bluetooth") {
                    const c = bleToCoords(bleItems, locations);
                    if (c) { lat = c.latitude; lon = c.longitude; }
                }
            } else if (payloadType === "event") {
                if (eventType === "Start of movement") { info['In Motion'] = "Yes"; markerColor = '#36ed6d'; }
                else if (eventType === "End of movement") { info['In Motion'] = "No"; markerColor = '#7eb8e0'; }
                resolveLvp(device?.lastValidPosition);
            }

            if (!lat || !lon) return null;
            return { device, lat, lon, markerColor, info, stale: isDeviceStale(device, now), positioningType };
        }).filter(Boolean);
    }, [props.devices, locations, gateways, centroids, now, props.wifiLocations]);

    // Pass 2: group by coordinate (5 decimal places ≈ 1 m precision)
    const locationGroups = useMemo(() => {
        const groups = {};
        for (const item of resolvedDevices) {
            const key = `${item.lat.toFixed(5)},${item.lon.toFixed(5)}`;
            if (!groups[key]) groups[key] = { lat: item.lat, lon: item.lon, items: [] };
            groups[key].items.push(item);
        }
        return groups;
    }, [resolvedDevices]);

    // Pass 3: render one marker per unique location
    const deviceMarkers = useMemo(() => {
        // Active marker last → renders last in DOM → paints over all other markers
        const entries = Object.entries(locationGroups);
        if (activeKey) {
            const idx = entries.findIndex(([k]) => k === activeKey);
            if (idx > -1) entries.push(entries.splice(idx, 1)[0]);
        }
        return entries.map(([coordKey, group]) => {
            const { lat, lon, items } = group;
            const isOpen = activeKey === coordKey;
            const Icon = icons[items[0]?.device?.meta?.deviceType] || TbWheelchair;

            // just one marker at the location
            if (items.length === 1) {
                const { device, markerColor, info, stale, positioningType: pt } = items[0];
                const deviceName = device?.meta?.deviceName;
                const ptBadge = POS_BADGE[pt];
                return (
                    <Overlay key={coordKey} anchor={[lat, lon]} offset={[0, 0]}>
                        <div
                            onClick={(e) => { e.stopPropagation(); setActiveKey(prev => prev === coordKey ? null : coordKey); }}
                            style={{ cursor: 'pointer', transform: 'translate(-50%, -100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'auto', position: 'relative', zIndex: isOpen ? 200 : 1 }}
                        >
                            {stale && (
                                <div className="stale-badge">
                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                                    </svg>
                                    5m+
                                </div>
                            )}
                            <div className="map-marker" style={{ background: markerColor }}>
                                <Icon className="icon" size={32} color="white" />
                            </div>
                            {isOpen && (
                                <div className="plan">
                                    <div className="inner">
                                        <div className="plan-header">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                                <span className="fw-semibold" style={{ fontSize: '0.875rem', color: '#1e293b' }}>{deviceName}</span>
                                                {ptBadge && (
                                                    <span style={{ background: ptBadge.bg, color: ptBadge.color, fontSize: '0.62rem', fontWeight: 700, padding: '2px 6px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                        {ptBadge.label}
                                                    </span>
                                                )}
                                            </div>
                                            <button className="plan-close" onClick={(e) => { e.stopPropagation(); setActiveKey(null); }}>&times;</button>
                                        </div>
                                        {Object.entries(info).map(([k, v]) => (
                                            <div key={k} className="info-row">
                                                <span className="info-key">{k}</span>
                                                <span className="info-value">{v}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Overlay>
                );
            }

            // Cluster: multiple devices at the same location
            const dominantColor = pickDominantColor(items.map(i => i.markerColor));
            const count = items.length;
            const staleCount = items.filter(i => i.stale).length;

            return (
                <Overlay key={coordKey} anchor={[lat, lon]} offset={[0, 0]}>
                    <div
                        onClick={(e) => { e.stopPropagation(); setClusterModal({ items, count }); }}
                        style={{ cursor: 'pointer', transform: 'translate(-50%, -100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'auto', position: 'relative' }}
                    >
                        {staleCount > 0 && (
                            <div className="stale-badge">
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                                </svg>
                                {staleCount === count ? 'all 5m+' : `${staleCount} of ${count} 5m+`}
                            </div>
                        )}
                        <div className="map-marker" style={{ background: dominantColor, opacity: 0.3, position: 'absolute', top: 6, left: 6, pointerEvents: 'none' }} />
                        <div className="map-marker" style={{ background: dominantColor, position: 'relative' }}>
                            <Icon className="icon" size={26} color="white" />
                            <span style={{
                                position: 'absolute', top: -7, right: -7, transform: 'rotate(45deg)',
                                background: '#111827', color: '#fff', fontSize: '0.62rem', fontWeight: 800,
                                width: 18, height: 18, borderRadius: '50%', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.45)', lineHeight: 1, border: '1.5px solid #fff',
                            }}>
                                {count}
                            </span>
                        </div>
                    </div>
                </Overlay>
            );
        });
    }, [locationGroups, activeKey]);

    const provider = useMemo(
        () => (satelliteView ? SATELLITE_PROVIDER : OSM_PROVIDER),
        [satelliteView]
    );

    return (
        <div style={{ height: '100%', width: '100%', position: 'relative', borderRadius: 10, overflow: 'hidden' }}>
            <Map
                center={mapCenter}
                zoom={zoom}
                onBoundsChanged={({ center, zoom }) => { setMapCenter(center); setZoom(zoom); }}
                defaultZoom={18}
                maxZoom={19}
                minZoom={5}
                provider={provider}
                onClick={() => setActiveKey(null)}
            >
                {deviceMarkers}
                <ZoomControl />
            </Map>

            {/* Overlay: map controls (top-right) */}
            <div className="map-overlay-controls">
                <button className="map-overlay-btn" onClick={resetMap} title="Recenter">
                    <FaRegCompass size={16} />
                </button>
                <button
                    className="map-overlay-btn"
                    onClick={() => setSatelliteView(prev => !prev)}
                    title={satelliteView ? "Switch to Street view" : "Switch to Satellite view"}
                >
                    {satelliteView ? <MdMap size={16} /> : <MdSatelliteAlt size={16} />}
                </button>
            </div>

            {/* Overlay: legend (bottom-left) */}
            <div className="map-overlay-legend">
                {LEGEND_ITEMS.map(({ color, label }) => (
                    <div key={label} className="map-overlay-legend-item">
                        <div className="map-overlay-legend-dot" style={{ background: color }} />
                        <span>{label}</span>
                    </div>
                ))}
            </div>

            {/* Cluster modal */}
            {clusterModal && (
                <div
                    onClick={() => setClusterModal(null)}
                    style={{
                        position: 'absolute', inset: 0, zIndex: 500,
                        background: 'rgba(10,18,36,0.55)',
                        backdropFilter: 'blur(3px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <div onClick={(e) => e.stopPropagation()} style={{
                        width: 500, maxWidth: '94vw', maxHeight: '80vh',
                        display: 'flex', flexDirection: 'column',
                        borderRadius: 16,
                        boxShadow: '0 24px 64px rgba(0,0,0,0.38), 0 4px 16px rgba(0,0,0,0.18)',
                        overflow: 'hidden',
                        background: '#fff',
                    }}>
                        {/* Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #1B4F8A 0%, #2563a8 100%)',
                            padding: '18px 20px 16px',
                            flexShrink: 0,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                                        <span style={{
                                            background: 'rgba(255,255,255,0.18)', border: '1.5px solid rgba(255,255,255,0.3)',
                                            borderRadius: 20, padding: '2px 10px',
                                            fontSize: '0.7rem', fontWeight: 700, color: '#fff', letterSpacing: '0.04em',
                                        }}>
                                            {clusterModal.count} ASSETS
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
                                        Assets at this location
                                    </div>
                                    <div style={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.65)', marginTop: 3 }}>
                                        {clusterModal.items[0]?.info?.['Location'] ?? '—'}
                                    </div>
                                </div>
                                <button
                                    onClick={() => setClusterModal(null)}
                                    style={{
                                        background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)',
                                        borderRadius: 8, cursor: 'pointer', color: '#fff',
                                        width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 18, lineHeight: 1, flexShrink: 0,
                                        transition: 'background 0.12s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                                >&times;</button>
                            </div>
                        </div>

                        {/* Device list */}
                        <div style={{ overflowY: 'auto', flex: 1 }}>
                            {clusterModal.items.map(({ device, markerColor: mc, info, positioningType: dpt, stale }, idx) => {
                                const dpBadge = POS_BADGE[dpt];
                                const location = info['Location'] ?? '—';
                                const lastMove = info['Last Movement'] ?? '—';
                                const isEven = idx % 2 === 0;
                                return (
                                    <div key={device.meta.deviceEui} style={{
                                        display: 'flex', alignItems: 'stretch',
                                        background: isEven ? '#fff' : '#f8fafd',
                                        borderBottom: '1px solid #eef2f8',
                                        transition: 'background 0.1s',
                                    }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#eef5ff'}
                                        onMouseLeave={e => e.currentTarget.style.background = isEven ? '#fff' : '#f8fafd'}
                                    >
                                        {/* Status stripe */}
                                        <div style={{ width: 4, background: mc, flexShrink: 0, borderRadius: idx === 0 ? '0' : '0' }} />

                                        {/* Content */}
                                        <div style={{ flex: 1, padding: '11px 16px', minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                                                <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {device.meta.deviceName}
                                                </span>
                                                {dpBadge && (
                                                    <span style={{
                                                        background: dpBadge.bg, color: dpBadge.color,
                                                        fontSize: '0.6rem', fontWeight: 700,
                                                        padding: '2px 7px', borderRadius: 20,
                                                        whiteSpace: 'nowrap', flexShrink: 0,
                                                    }}>
                                                        {dpBadge.label}
                                                    </span>
                                                )}
                                                {stale && (
                                                    <span style={{
                                                        background: '#fef3c7', color: '#92400e',
                                                        fontSize: '0.58rem', fontWeight: 700,
                                                        padding: '2px 6px', borderRadius: 20,
                                                        whiteSpace: 'nowrap', flexShrink: 0,
                                                    }}>5m+</span>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                                                    </svg>
                                                    <span style={{ fontSize: '0.75rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>{location}</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                                                    </svg>
                                                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{lastMove}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: '10px 20px', background: '#f8fafd',
                            borderTop: '1px solid #eef2f8', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                Click outside or × to close
                            </span>
                            <div style={{ display: 'flex', gap: 12 }}>
                                {Object.entries(
                                    clusterModal.items.reduce((acc, { markerColor: mc }) => {
                                        const label = LEGEND_ITEMS.find(l => l.color === mc)?.label ?? 'Other';
                                        acc[label] = (acc[label] ?? 0) + 1;
                                        return acc;
                                    }, {})
                                ).map(([label, n]) => (
                                    <span key={label} style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>
                                        {label}: {n}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AssetTrackingMap;
